import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/**
 * 다크모드 테마 상태.
 * - mode: 사용자 선택 'light' | 'dark' | 'system' (localStorage `rhoot_theme`, 기본 'system')
 * - theme: 실효 테마 'light' | 'dark' (system 이면 OS prefers-color-scheme 해석)
 * - 실효 테마는 document.documentElement.dataset.theme 에 스탬프되어
 *   src/styles/dark-theme.css 의 `:root[data-theme='dark']` 오버라이드가 적용된다.
 *
 * FOUC 방지: index.js 렌더 전에 initTheme() 를 호출해 첫 페인트 전에 스탬프한다.
 */

const STORAGE_KEY = 'rhoot_theme';
const MODES = ['light', 'dark', 'system']; // cycleMode 순환 순서 (라이트 → 다크 → 시스템)

export const THEME_MODE_LABEL = { light: '라이트', dark: '다크', system: '시스템' };

function readStoredMode() {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return MODES.includes(v) ? v : 'system';
  } catch (e) {
    return 'system';
  }
}

function writeStoredMode(mode) {
  try { window.localStorage.setItem(STORAGE_KEY, mode); } catch (e) { /* noop */ }
}

function systemPrefersDark() {
  try {
    return !!(window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches);
  } catch (e) {
    return false;
  }
}

/** mode('light'|'dark'|'system') → 실효 테마('light'|'dark') */
export function resolveTheme(mode) {
  if (mode === 'light' || mode === 'dark') return mode;
  return systemPrefersDark() ? 'dark' : 'light';
}

function stampTheme(resolved) {
  try { document.documentElement.dataset.theme = resolved; } catch (e) { /* noop */ }
}

/** index.js 초기화 시점(렌더 전)에 호출 — 첫 페인트 전에 data-theme 스탬프 (FOUC 방지) */
export function initTheme() {
  stampTheme(resolveTheme(readStoredMode()));
}

const ThemeContext = createContext({
  mode: 'system',
  theme: 'light',
  setMode: () => {},
  cycleMode: () => {},
});

export function ThemeProvider({ children }) {
  const [mode, setModeState] = useState(readStoredMode);
  const [theme, setTheme] = useState(() => resolveTheme(readStoredMode()));

  // mode 변경 시 실효 테마 재계산 + 스탬프. system 이면 OS 테마 변경(matchMedia) 추적.
  useEffect(() => {
    const apply = () => {
      const resolved = resolveTheme(mode);
      setTheme(resolved);
      stampTheme(resolved);
    };
    apply();

    if (mode !== 'system' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => apply();
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else if (mq.addListener) mq.addListener(onChange); // 구형 Safari 폴백
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else if (mq.removeListener) mq.removeListener(onChange);
    };
  }, [mode]);

  const setMode = useCallback((next) => {
    const v = MODES.includes(next) ? next : 'system';
    writeStoredMode(v);
    setModeState(v);
  }, []);

  // localStorage 쓰기(부수효과)는 setState updater 밖에서 — StrictMode 등에서
  // updater 가 중복 호출돼도 안전하도록 다음 모드를 현재 mode 로부터 계산해 저장한다.
  const cycleMode = useCallback(() => {
    const next = MODES[(MODES.indexOf(mode) + 1) % MODES.length];
    writeStoredMode(next);
    setModeState(next);
  }, [mode]);

  const value = useMemo(
    () => ({ mode, theme, setMode, cycleMode }),
    [mode, theme, setMode, cycleMode],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/** 어디서나 { mode, theme, setMode, cycleMode } 사용. Provider 밖이면 light 기본값. */
export default function useTheme() {
  return useContext(ThemeContext);
}
